import React, { useState, useCallback, useContext, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { Box } from '@radix-ui/themes';

import CampaignDates from './CampaignDates.jsx';
import ClientsRowAction from './ClientsRowAction.jsx';
import Tooltip from '../../../../shared/Tooltip';
import { useNotification } from '../../../../shared/Notification/useNotification';
import { ActionCableContext } from '../../../../lib/actionCableContext';
import {
  useGetAllClients,
  useGetPaginatedClients,
  editClientDate,
  archiveCampaign,
  unarchiveCampaign,
  duplicateCampaign,
} from '../../data';
import Card from '../../../../shared/Card';
import {
  Table,
  Tr,
  Th,
  ThContentWrapper,
  ColumnTitle,
  ShadedTr,
  Td,
  Paragraph,
  ThContentWrapperRight,
  TdRight,
  Link,
} from '../../../../shared/TableComponents/StyledComponents';
import Sort from '../../../../shared/Sort';
import Placeholder from '../../../../shared/Placeholder';
import { AddSeats } from './AddSeats.jsx';
import EditDateCell from './EditDateCell.jsx';
import ModalConfirmation from '../../../../shared/ModalConfirmation';
import Modal from '../../../../shared/Modal';

const ClientsCard = styled(Card)`
  background: ${themeGet('colors.white')};
`;

const TdClients = styled(Td)`
  padding: 0;
  &:first-child {
    padding: 0 0 0 24px;
  }
  &:last-child {
    padding: 0 24px 0 0;
  }
`;

const TdRightClients = styled(TdRight)`
  padding: 0;
  p {
    text-align: right;
  }
  &:first-child {
    padding: 0 0 0 24px;
  }
  &:last-child {
    padding: 0 24px 0 0;
  }
`;

const Content = styled.div`
  p {
    margin-top: 14px;
    margin-bottom: 14px;
  }
`;

const ThClients = styled(Th)`
  &:first-child {
    padding: 0 15px 16px 24px;
  }
  &:last-child {
    padding: ${props => (props.last ? '0 24px 16px 0' : '0 0 16px 0')};
  }
`;

const ParagraphClients = styled(Paragraph)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TdCompanyName = styled(Content)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const messages = {
  finished_archive: 'clients:clientsTable:archive',
  archive_error: 'clients:clientsTable:archiveError',
  finished_duplicate: 'clients:clientsTable:duplicate',
  finished_create: 'clients:clientsTable:create',
};

const ClientsTable = ({
  data,
  sortingStatus,
  changeSort,
  availableSeats,
  totalSeats,
  filter,
}) => {
  const { showSuccess, showDefault } = useNotification();
  const { t } = useTranslation(['shared', 'clients']);
  const queryClient = useQueryClient();
  const cable = useContext(ActionCableContext);

  useEffect(() => {
    const channel = cable.subscriptions.create(
      {
        channel: 'BoardChannel',
        id: 1,
      },
      {
        received: response => {
          if (messages[response.status]) {
            showSuccess({ content: t(messages[response.status]) });
            queryClient.invalidateQueries(['clients']);
          }
        },
      },
    );
    return () => {
      channel.unsubscribe();
    };
  }, [cable, queryClient, showSuccess, t]);

  const { mutate } = useMutation(editClientDate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['clients']);
      showSuccess({ content: t('clients:clientsTable:update') });
      Swal.close();
    },
  });

  const { mutate: archiveMutate } = useMutation(archiveCampaign, {
    onSuccess: () => {
      showDefault({
        content: t('clients:clientsTable:pending'),
        title: t('clients:clientsTable:archive'),
      });
    },
  });

  const { mutate: unarchiveMutate } = useMutation(unarchiveCampaign, {
    onSuccess: (response, variables) => {
      const { name } = variables;
      queryClient.invalidateQueries(['clients']);

      showSuccess({
        title: t('clients:unarchiveModal:header'),
        content: t('clients:unarchiveModal:body', { boardName: name }),
      });
    },
  });

  const { mutate: duplicateMutate } = useMutation(duplicateCampaign, {
    onSuccess: () => {
      showDefault({
        content: t('clients:clientsTable:pending'),
        title: t('clients:clientsTable:duplicate'),
      });
    },
  });

  const showModal = useCallback(
    (id, dateRange) => () => {
      const onSave = dates => {
        mutate({
          id,
          beginsAt: dates[0],
          endsAt: dates[1],
        });
      };

      Modal({
        component: (
          <CampaignDates
            onCancel={() => Swal.clickCancel()}
            onSave={onSave}
            initialDates={dateRange}
            title="Campaign Dates"
          />
        ),
        padding: '24px 0px 13px',
        width: '394px',
      });
    },
    [mutate],
  );

  const confirmArchiveCampaign = useCallback(
    ({ uuid, name }) => () => {
      const onSave = () => {
        archiveMutate(uuid);
      };

      Modal({
        component: (
          <ModalConfirmation
            onSave={onSave}
            header={t('clients:clientsTable:archiveHeader')}
            body={t('clients:clientsTable:archiveBody', { boardName: name })}
            variant={'containedRed'}
            answer={t('clients:clientsTable:archiveAnswerValue')}
            actionTitle={t('clients:clientsTable:archiveTooltip')}
            submitText={t('clients:clientsTable.archiveButton')}
            cancelText={t('clients:clientsTable.cancelButton')}
          />
        ),
      });
    },
    [archiveMutate, t],
  );

  const unarchiveCampaignAction = useCallback(
    ({ uuid, name }) => () => {
      unarchiveMutate({ uuid, name });
    },
    [unarchiveMutate],
  );

  const duplicateCampaignAction = useCallback(
    id => () => {
      duplicateMutate({ id });
    },
    [duplicateMutate],
  );

  const showRowAction = row => {
    return (
      <TdClients>
        <Content>
          <ClientsRowAction
            filter={filter}
            campaign={row}
            archiveCampaignAction={confirmArchiveCampaign(row)}
            duplicateCampaignAction={duplicateCampaignAction(row.uuid)}
            unarchiveCampaignAction={unarchiveCampaignAction(row)}
          />
        </Content>
      </TdClients>
    );
  };

  const showEditDate = (date, uuid, campaignRange) => {
    if (filter === 'archived') {
      return (
        <ParagraphClients>
          {date ? moment(date).format('M/D/YY') : '-'}
        </ParagraphClients>
      );
    }

    return (
      <EditDateCell date={date} showModal={showModal(uuid, campaignRange)} />
    );
  };

  const renderColumn = () => {
    return <col span="1" style={{ width: '11%' }}></col>;
  };

  const formattedAvailableSeats =
    availableSeats > 0 ? availableSeats.toLocaleString() : '0';
  const formattedTotalSeats =
    totalSeats > 0 ? totalSeats.toLocaleString() : '0';

  return (
    <React.Fragment>
      <Box position="relative">
        <Tooltip />
        <Table>
          <colgroup>
            <col span="1" style={{ width: '15%' }}></col>
            <col span="1" style={{ width: '20%' }}></col>
            <col span="1" style={{ width: '11%' }}></col>
            <col span="1" style={{ width: '10%' }}></col>
            <col span="1" style={{ width: '10%' }}></col>
            <col span="1" style={{ width: '10%' }}></col>
            <col span="1" style={{ width: '13%' }}></col>
            {renderColumn()}
          </colgroup>
          <thead>
            <Tr>
              <ThClients>
                <ThContentWrapper onClick={() => changeSort('companyName')}>
                  <Sort
                    sortingStatus={sortingStatus}
                    columnName="companyName"
                  />
                  <ColumnTitle>Client</ColumnTitle>
                </ThContentWrapper>
              </ThClients>
              <Th>
                <ThContentWrapper onClick={() => changeSort('boardName')}>
                  <Sort sortingStatus={sortingStatus} columnName="boardName" />
                  <ColumnTitle>Campaign</ColumnTitle>
                </ThContentWrapper>
              </Th>
              <Th>
                <ThContentWrapperRight onClick={() => changeSort('createdAt')}>
                  <ColumnTitle>Date Created</ColumnTitle>
                  <Sort sortingStatus={sortingStatus} columnName="createdAt" />
                </ThContentWrapperRight>
              </Th>
              <Th>
                <ThContentWrapperRight onClick={() => changeSort('beginsAt')}>
                  <ColumnTitle>Start Date</ColumnTitle>
                  <Sort sortingStatus={sortingStatus} columnName="beginsAt" />
                </ThContentWrapperRight>
              </Th>
              <Th>
                <ThContentWrapperRight onClick={() => changeSort('endsAt')}>
                  <ColumnTitle>End Date</ColumnTitle>
                  <Sort sortingStatus={sortingStatus} columnName="endsAt" />
                </ThContentWrapperRight>
              </Th>
              <Th>
                <ThContentWrapperRight
                  onClick={() => changeSort('eligibleEmployees')}
                >
                  <ColumnTitle>Seats</ColumnTitle>
                  <Sort
                    sortingStatus={sortingStatus}
                    columnName="eligibleEmployees"
                  />
                </ThContentWrapperRight>
              </Th>
              <ThClients>
                <ThContentWrapperRight onClick={() => changeSort('usersCount')}>
                  <ColumnTitle>Active Users</ColumnTitle>
                  <Sort sortingStatus={sortingStatus} columnName="usersCount" />
                </ThContentWrapperRight>
              </ThClients>
            </Tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              const campaignRange = row.beginsAt
                ? [new Date(row.beginsAt), new Date(row.endsAt)]
                : [];

              return (
                <ShadedTr key={i}>
                  <TdClients>
                    <Content>
                      <ParagraphClients
                        data-tip={row.clientName}
                        data-offset="{ 'left': 40 }"
                      >
                        {row.clientName || '\u00A0'}
                      </ParagraphClients>
                    </Content>
                  </TdClients>
                  <TdClients>
                    <TdCompanyName>
                      <ParagraphClients>
                        <Link
                          rel="nofollow"
                          data-method="patch"
                          href={`/current_board?board_id=${row.uuid}&admin_view_board=true`}
                          data-tip={row.name}
                        >
                          {row.name}
                        </Link>
                      </ParagraphClients>
                    </TdCompanyName>
                  </TdClients>
                  <TdRightClients>
                    <Content>
                      <ParagraphClients>
                        {moment(row.createdAt).format('M/D/YY')}
                      </ParagraphClients>
                    </Content>
                  </TdRightClients>
                  <TdRightClients>
                    <Content>
                      {showEditDate(row.beginsAt, row.uuid, campaignRange)}
                    </Content>
                  </TdRightClients>
                  <TdRightClients>
                    <Content>
                      {showEditDate(row.endsAt, row.uuid, campaignRange)}
                    </Content>
                  </TdRightClients>
                  <TdRightClients>
                    <Content>
                      <ParagraphClients>
                        {row.eligibleEmployees || '\u00A0'}
                      </ParagraphClients>
                    </Content>
                  </TdRightClients>
                  <TdRightClients>
                    <Content>
                      <ParagraphClients>
                        {row.usersCount || '\u00A0'}
                      </ParagraphClients>
                    </Content>
                  </TdRightClients>
                  {showRowAction(row)}
                </ShadedTr>
              );
            })}
          </tbody>
        </Table>

        <Box position="absolute" top="-40px" right="20px">
          <AddSeats
            availableSeats={formattedAvailableSeats}
            totalSeats={formattedTotalSeats}
          />
        </Box>
      </Box>
    </React.Fragment>
  );
};

export const ClientsTablePaginated = ({
  onClick,
  sortingStatus,
  changeSort,
  filter,
}) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;
  const onPageChange = data => setPage(data.selected + 1);
  const {
    isLoading,
    data = { clients: [], pagy: {} },
  } = useGetPaginatedClients({
    page,
    itemsPerPage,
    filter,
    ...sortingStatus,
  });

  const {
    clients,
    pagy,
    available_seats: availableSeats,
    total_seats: totalSeats,
  } = data;

  return (
    <ClientsCard
      title="Campaigns"
      items={itemsPerPage}
      onClick={onClick}
      canShowAll={true}
      isShowingAll={false}
      isLoading={isLoading}
      pagy={pagy}
      onPageChange={onPageChange}
    >
      {clients.length ? (
        <ClientsTable
          data={clients}
          sortingStatus={sortingStatus}
          changeSort={changeSort}
          totalSeats={totalSeats}
          availableSeats={availableSeats}
          filter={filter}
        />
      ) : (
        <Placeholder />
      )}
    </ClientsCard>
  );
};

export const ClientsTableAll = ({
  onClick,
  sortingStatus,
  changeSort,
  filter,
}) => {
  const { isLoading, data = { clients: [] } } = useGetAllClients({
    filter,
    ...sortingStatus,
  });

  return (
    <ClientsCard
      title="Campaigns"
      total={data.clients.length}
      canShowAll={true}
      isShowingAll={true}
      isLoading={isLoading}
      onClick={onClick}
    >
      {data.clients.length ? (
        <ClientsTable
          data={data.clients}
          sortingStatus={sortingStatus}
          changeSort={changeSort}
          totalSeats={data.total_seats}
          availableSeats={data.available_seats}
          filter={filter}
        />
      ) : (
        <Placeholder />
      )}
    </ClientsCard>
  );
};
